import { decode } from 'html-entities';
import striptags from 'striptags';
import { objEquals } from 'sutl';
const nonTelephonyDigits = /[^\d*#]+/g;
const numericIpAddressRegex = /(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])(?:\.(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])){3}/;
const phoneNumberRegex = /\+?(?:\d{1,3}-)?\d{3}-\d{3}-\d{4}|\+?(?:\d{1,3}\.)?\d{3}\.\d{3}\.\d{4}|\+?(?:\d{1,3}\s+)?\d{3}\s+\d{3}\s+\d{4}|\+?(?:\d{1,3}\s+)?\(\d{3}\)\s+\d{3}\s+\d{4}|\+?(?:\d{1,3}\s+)?\(\d{3}\)\s+\d{3}-\d{4}|\+?\d{10,13}/;
class BlueJeansParser {
    constructor() {
        this.blueJeansRegex = new RegExp('https://bluejeans\\.com/(\\d+)(?:.|[\r\n])+' +
            'Dial:?\\s+(' +
            numericIpAddressRegex.source +
            ')(?:.|[\r\n])*?' +
            '(' +
            phoneNumberRegex.source +
            ')\\s+\\(US Toll Free\\)', 'i');
        this.parserName = 'BlueJeans';
    }
    getCallMethods(str) {
        const callMethods = [];
        if (str === undefined)
            return undefined;
        const match = this.blueJeansRegex.exec(str);
        if (!match)
            return undefined;
        const [, meetingRoom, videoNumber, displayAudioNumber] = match;
        const audioNumber = displayAudioNumber.replace(nonTelephonyDigits, '');
        callMethods.push({
            callType: 'video',
            dialString: videoNumber,
            meetingCodes: {
                a0: `${meetingRoom}#`,
            },
        });
        for (const callType of ['pstn', 'voip']) {
            callMethods.push({
                callType,
                displayDialString: `${displayAudioNumber},${meetingRoom}#`,
                dialString: audioNumber,
                meetingCodes: {
                    a0: `${meetingRoom}#`,
                },
            });
        }
        return callMethods;
    }
    parse(event, currentCallMethods) {
        var _a, _b, _c;
        return [
            (_c = (_b = this.getCallMethods((_a = event.location) === null || _a === void 0 ? void 0 : _a.name)) !== null && _b !== void 0 ? _b : this.getCallMethods(getDescription(event))) !== null && _c !== void 0 ? _c : [],
            false,
        ];
    }
}
class GeneralParser {
    constructor() {
        this.dialStringMeetingCodeRegex = /(\d+)@.+|.+##(\d+)/;
        this.generalMeetingIdLine = /(?:conference\s*id|meeting\s*id|webinar\s*id|access\s*code|pass\s*code)\D*:?\s*([\d#][-\d #]+[\d#])/gi;
        this.generalPrefixMap = {
            audio: ['pstn', 'voip'],
            video: ['video'],
            pstn: ['pstn'],
            tel: ['pstn', 'voip'],
            sip: ['voip'],
            voip: ['voip'],
            voice: ['pstn', 'voip'],
            zoom: ['zoom'],
            sfb: undefined,
            'meeting id': undefined,
            'conference id': undefined,
            'access code': undefined,
        };
        this.parserName = 'General';
        this.textIpAddressRegex = /\w{2,}(?:\.\w{2,})*(?:\.[a-zA-Z]{2,})/;
        this.ipAddressRegex = new RegExp('(?:' + numericIpAddressRegex.source + ')|(?:' + this.textIpAddressRegex.source + ')');
        this.phoneMeetingCodesRegex = /(?:,+[\d#]+)*/;
        this.generalCallMethodRegex = new RegExp('(?:(audio|video|pstn|tel|sip|voip|voice|zoom|sfb)(?::\\s*|\\s+))?' +
            '(?:' +
            '(?:((?:' +
            this.ipAddressRegex.source +
            ')(?:##\\d+)*))|' + // group 2
            '(?:(\\d+@(?:' +
            this.ipAddressRegex.source +
            ')))|' + // group 3
            '(?:(' +
            phoneNumberRegex.source +
            ')(' +
            this.phoneMeetingCodesRegex.source +
            '))' + // group 4, 5
            '|(?:(\\d{3,9})(' +
            this.phoneMeetingCodesRegex.source +
            '))' + // group 6, 7
            ')', 'ig');
    }
    dialStringHasMeetingCodes(dialString, meetingCodes) {
        var _a;
        if (meetingCodes === undefined || Object.keys(meetingCodes).length > 1)
            return false;
        const match = this.dialStringMeetingCodeRegex.exec(dialString);
        const meetingCode = (_a = meetingCodes.a0) === null || _a === void 0 ? void 0 : _a.replace(/#+$/, '');
        if (match && (match[1] === meetingCode || match[2] === meetingCode))
            return true;
        return false;
    }
    getGeneralCallTypes(callType, match) {
        if (match) {
            match = match.toLowerCase();
            if (this.generalPrefixMap.hasOwnProperty(match)) {
                return this.generalPrefixMap[match];
            }
        }
        return Array.isArray(callType) ? callType : [callType];
    }
    getMeetingCodeFromLines(meetingIdLines, pos) {
        let meetingCodes = undefined;
        let closestCode = undefined;
        let closestDist = Number.MAX_SAFE_INTEGER;
        // Find nearest meeting code.
        for (const line of meetingIdLines) {
            const dist = Math.abs(pos - line.pos);
            if (closestCode === undefined || dist < closestDist) {
                closestCode = line.code;
                closestDist = dist;
            }
        }
        if (closestCode !== undefined) {
            let code = closestCode;
            if (code[code.length - 1] !== '#')
                code += '#';
            meetingCodes = { a0: code };
        }
        return meetingCodes;
    }
    isMeetingCode(meetingIdLines, pos) {
        for (const line of meetingIdLines) {
            const dist = pos - line.pos;
            if (dist > 0 && dist <= 16)
                return true;
        }
        return false;
    }
    getCallMethods(str) {
        if (str === undefined)
            return undefined;
        this.generalMeetingIdLine.lastIndex = 0;
        this.generalCallMethodRegex.lastIndex = 0;
        const callMethods = [];
        let match = null;
        const meetingIdLines = [];
        while ((match = this.generalMeetingIdLine.exec(str)) !== null) {
            const code = match[1].replace(nonTelephonyDigits, '');
            if (code.length < 5)
                continue;
            meetingIdLines.push({
                pos: match.index,
                code: match[1].replace(nonTelephonyDigits, ''),
            });
        }
        while ((match = this.generalCallMethodRegex.exec(str)) !== null) {
            if (match[2] !== undefined) {
                // 123.123.123.123##4567 (code is optional)
                // Don't match ip address without prefix or meeting code.
                if (match[1] === undefined && !this.dialStringMeetingCodeRegex.exec(match[2])) {
                    continue;
                }
                const callTypes = this.getGeneralCallTypes('video', match[1]);
                if (callTypes === undefined)
                    continue;
                const meetingCodes = this.getMeetingCodeFromLines(meetingIdLines, match.index);
                for (const callType of callTypes) {
                    const callMethod = {
                        callType,
                        dialString: match[2],
                    };
                    if (meetingCodes !== undefined &&
                        !this.dialStringHasMeetingCodes(callMethod.dialString, meetingCodes)) {
                        callMethod.meetingCodes = Object.assign({}, meetingCodes);
                    }
                    callMethods.push(callMethod);
                }
            }
            else if (match[3] !== undefined) {
                // 1234@123.123.123.123
                const callTypes = this.getGeneralCallTypes('video', match[1]);
                if (callTypes === undefined)
                    continue;
                const meetingCodes = this.getMeetingCodeFromLines(meetingIdLines, match.index);
                for (const callType of callTypes) {
                    const callMethod = {
                        callType,
                        dialString: match[3],
                    };
                    if (meetingCodes !== undefined &&
                        !this.dialStringHasMeetingCodes(callMethod.dialString, meetingCodes)) {
                        callMethod.meetingCodes = Object.assign({}, meetingCodes);
                    }
                    callMethods.push(callMethod);
                }
            }
            else if ((match[4] !== undefined || match[6] !== undefined) &&
                !this.isMeetingCode(meetingIdLines, match.index)) {
                // phone number
                const callTypes = this.getGeneralCallTypes(['pstn', 'voip'], match[1]);
                if (callTypes === undefined)
                    continue;
                const isPhoneNumber = match[4] !== undefined;
                // If it is just an extension, then it must include a prefix.
                if (!isPhoneNumber && match[1] === undefined)
                    continue;
                let displayDialString = isPhoneNumber ? match[4] : match[6];
                const meetingCodesStr = isPhoneNumber ? match[5] : match[7];
                const dialString = displayDialString.replace(nonTelephonyDigits, '');
                let meetingCodes;
                if (meetingCodesStr !== undefined && meetingCodesStr !== '') {
                    const m = meetingCodesStr.replace(/^,+/, '').split(/,+/);
                    meetingCodes = Object.fromEntries(m.map((code, i) => [`a${i}`, code]));
                }
                if (meetingCodes === undefined) {
                    meetingCodes = this.getMeetingCodeFromLines(meetingIdLines, match.index);
                }
                if (!displayDialString.includes(',') && meetingCodes !== undefined) {
                    for (const id of Object.keys(meetingCodes).sort()) {
                        displayDialString += `,${meetingCodes[id]}`;
                    }
                }
                for (const callType of callTypes) {
                    const callMethod = {
                        callType,
                        dialString,
                    };
                    if (displayDialString !== dialString) {
                        callMethod.displayDialString = displayDialString;
                    }
                    if (meetingCodes !== undefined) {
                        callMethod.meetingCodes = Object.assign({}, meetingCodes);
                    }
                    callMethods.push(callMethod);
                }
            }
        }
        return callMethods;
    }
    parse(event, currentCallMethods) {
        var _a, _b, _c;
        return [
            [
                ...((_b = this.getCallMethods((_a = event.location) === null || _a === void 0 ? void 0 : _a.name)) !== null && _b !== void 0 ? _b : []),
                ...((_c = this.getCallMethods(getDescription(event))) !== null && _c !== void 0 ? _c : []),
            ],
            false,
        ];
    }
}
class GoogleMeetParser {
    constructor() {
        this.parserName = 'GoogleMeet';
        this.googleMeetRegex = new RegExp(/https:\/\/meet.google.com\/([A-Za-z0-9\-]+)/);
    }
    getCallMethods(str) {
        const callMethods = [];
        if (str === undefined)
            return undefined;
        const match = this.googleMeetRegex.exec(str);
        if (!match)
            return undefined;
        callMethods.push({
            callType: 'googleMeet',
            dialString: match[1],
        });
        return callMethods;
    }
    parse(event, currentCallMethods) {
        var _a;
        return [(_a = this.getCallMethods(getDescription(event, false))) !== null && _a !== void 0 ? _a : [], true];
    }
}
class GoToMeetingParser {
    constructor() {
        this.goToMeetingRegex = new RegExp('https://global.gotomeeting.com/join/(d+)(?:.|[\r\n])*?' +
            '(' +
            phoneNumberRegex.source +
            ')', 'i');
        this.parserName = 'GoToMeeting';
    }
    getCallMethods(str) {
        const callMethods = [];
        if (str === undefined)
            return undefined;
        const match = this.goToMeetingRegex.exec(str);
        if (!match)
            return undefined;
        const [, code, displayDialString] = match;
        const dialString = displayDialString.replace(nonTelephonyDigits, '');
        for (const callType of ['pstn', 'voip']) {
            callMethods.push({
                callType,
                displayDialString: `${displayDialString},${code}#`,
                dialString,
                meetingCodes: {
                    a0: `${code}#`,
                },
            });
        }
        return callMethods;
    }
    parse(event, currentCallMethods) {
        var _a, _b, _c;
        return [
            (_c = (_b = this.getCallMethods((_a = event.location) === null || _a === void 0 ? void 0 : _a.name)) !== null && _b !== void 0 ? _b : this.getCallMethods(getDescription(event))) !== null && _c !== void 0 ? _c : [],
            false,
        ];
    }
}
class LyncParser {
    constructor() {
        this.lyncRegex = /https:\/\/meet\.lync\.com\/([^/]+)\/([^/]+)\/([a-zA-Z0-9]+)/;
        this.parserName = 'Lync';
        this.tlds = [
            'ac',
            'ad',
            'ae',
            'af',
            'ag',
            'ai',
            'al',
            'am',
            'ao',
            'aq',
            'ar',
            'as',
            'at',
            'au',
            'aw',
            'ax',
            'az',
            'ba',
            'bb',
            'bd',
            'be',
            'bf',
            'bg',
            'bh',
            'bi',
            'bj',
            'bm',
            'bn',
            'bo',
            'br',
            'bs',
            'bt',
            'bw',
            'by',
            'bz',
            'ca',
            'cc',
            'cd',
            'cf',
            'cg',
            'ch',
            'ci',
            'ck',
            'cl',
            'cm',
            'cn',
            'co',
            'cr',
            'cu',
            'cv',
            'cw',
            'cx',
            'cy',
            'cz',
            'de',
            'dj',
            'dk',
            'dm',
            'do',
            'dz',
            'ec',
            'ee',
            'eg',
            'es',
            'et',
            'et',
            'eu',
            'fi',
            'fj',
            'fk',
            'fm',
            'fo',
            'fr',
            'ga',
            'gd',
            'ge',
            'gf',
            'gg',
            'gh',
            'gi',
            'gl',
            'gm',
            'gn',
            'gp',
            'gq',
            'gr',
            'gs',
            'gt',
            'gu',
            'gw',
            'gy',
            'hk',
            'hm',
            'hn',
            'hr',
            'ht',
            'hu',
            'id',
            'ie',
            'il',
            'im',
            'in',
            'io',
            'iq',
            'ir',
            'is',
            'it',
            'je',
            'jm',
            'jo',
            'jp',
            'ke',
            'kg',
            'kh',
            'ki',
            'km',
            'kn',
            'kp',
            'kr',
            'kw',
            'ky',
            'kz',
            'la',
            'lb',
            'lc',
            'li',
            'lk',
            'lr',
            'ls',
            'lt',
            'lu',
            'lv',
            'ly',
            'ma',
            'mc',
            'md',
            'me',
            'mg',
            'mh',
            'mk',
            'ml',
            'mm',
            'mn',
            'mo',
            'mp',
            'mq',
            'mr',
            'ms',
            'mt',
            'mu',
            'mv',
            'mw',
            'mx',
            'my',
            'mz',
            'na',
            'nc',
            'ne',
            'nf',
            'ng',
            'ni',
            'nl',
            'no',
            'np',
            'nr',
            'nu',
            'nz',
            'om',
            'pa',
            'pe',
            'pf',
            'pg',
            'ph',
            'pk',
            'pl',
            'pm',
            'pn',
            'pr',
            'ps',
            'pt',
            'pw',
            'py',
            'qq',
            're',
            'ro',
            'rs',
            'ru',
            'rw',
            'sa',
            'sb',
            'sc',
            'sd',
            'se',
            'sg',
            'sh',
            'si',
            'sk',
            'sh',
            'si',
            'sk',
            'sl',
            'sm',
            'sn',
            'so',
            'sr',
            'ss',
            'st',
            'su',
            'sv',
            'sx',
            'sy',
            'sz',
            'tc',
            'td',
            'tf',
            'tg',
            'th',
            'tj',
            'tk',
            'tl',
            'tm',
            'tn',
            'to',
            'tr',
            'tt',
            'tv',
            'tw',
            'tw',
            'tz',
            'ua',
            'ug',
            'uk',
            'us',
            'uy',
            'uz',
            'va',
            'vc',
            've',
            'vg',
            'vi',
            'vn',
            'vu',
            'wf',
            'ws',
            'ye',
            'yt',
            'za',
            'zm',
            'zw',
            // ---
            'com',
            'org',
            'net',
            'int',
            'edu',
            'gov',
            'mil',
        ];
    }
    getCallMethods(str) {
        var _a;
        const callMethods = [];
        if (str === undefined)
            return undefined;
        const match = this.lyncRegex.exec(str);
        if (!match)
            return undefined;
        const [dialString, , user, id] = match;
        let [, domain] = match;
        // SNTY-1492
        if (domain.includes('-')) {
            const parts = domain.split('-');
            const tld = (_a = parts.pop()) !== null && _a !== void 0 ? _a : '';
            if (this.tlds.includes(tld))
                domain = `${parts.join('-')}.${tld}`;
        }
        // SNTY-1372
        if (!domain.includes('.'))
            domain += '.com';
        callMethods.push({
            callType: 'teams',
            dialString,
            displayDialString: id,
        });
        callMethods.push({
            callType: 'video',
            dialString: `sip:${user}@${domain};gruu;opaque=app:conf:focus:id:${id}`,
            displayDialString: `${user}@${domain}`,
        });
        return callMethods;
    }
    parse(event, currentCallMethods) {
        var _a, _b, _c;
        return [
            [
                ...((_b = this.getCallMethods((_a = event.location) === null || _a === void 0 ? void 0 : _a.name)) !== null && _b !== void 0 ? _b : []),
                ...((_c = this.getCallMethods(getDescription(event, false))) !== null && _c !== void 0 ? _c : []),
            ],
            true,
        ];
    }
}
class PlcmTeamsParser {
    constructor() {
        this.parserName = 'PlcmTeams';
        this.plcmTeamsRegex = /(\d+)@(t.plcm.vc)[\s\S]+?(?:video\s*?)?conference\s*?id[:\s]*?([\d][\s\d]+[\d#])/i;
    }
    getCallMethods(str) {
        const callMethods = [];
        if (str === undefined)
            return undefined;
        const match = this.plcmTeamsRegex.exec(str);
        if (!match)
            return undefined;
        callMethods.push({
            callType: 'video',
            dialString: `${match[1]}.${match[3].replace(/\s+/g, '')}@${match[2]}`,
        });
        return callMethods;
    }
    parse(event, currentCallMethods) {
        var _a, _b;
        const locationCallMethods = this.getCallMethods((_a = event.location) === null || _a === void 0 ? void 0 : _a.name);
        if (locationCallMethods !== undefined)
            return [locationCallMethods, false];
        return [(_b = this.getCallMethods(getDescription(event))) !== null && _b !== void 0 ? _b : [], true];
    }
}
class PolycomParser {
    constructor() {
        this.parserName = 'Polycom';
        this.polycomRegex1 = new RegExp('(\\d+)@(' +
            numericIpAddressRegex.source +
            ').+' +
            'https://meetme.polycom.com/\\1.+' +
            'Audio:*\\s*(' +
            phoneNumberRegex.source +
            '),+\\1#', 'i');
        this.polycomRegex2 = new RegExp('(' +
            numericIpAddressRegex.source +
            ')##(\\d+).+' +
            'Audio:*\\s*(' +
            phoneNumberRegex.source +
            '),+\\2#.+' +
            'https://meetme.polycom.com/\\2', 'i');
    }
    getCallMethods(str) {
        const callMethods = [];
        if (str === undefined)
            return undefined;
        let matchNum = 1;
        let match = this.polycomRegex1.exec(str);
        if (!match) {
            matchNum = 2;
            match = this.polycomRegex2.exec(str);
        }
        if (!match)
            return undefined;
        const meetingRoom = matchNum === 1 ? match[1] : match[2];
        const videoNumber = matchNum === 1 ? match[2] : match[1];
        const displayAudioNumber = match[3];
        const audioNumber = displayAudioNumber.replace(nonTelephonyDigits, '');
        const videoDialString = matchNum === 1 ? `${meetingRoom}@${videoNumber}` : `${videoNumber}##${meetingRoom}`;
        callMethods.push({
            callType: 'video',
            dialString: videoDialString,
        });
        for (const callType of ['pstn', 'voip']) {
            callMethods.push({
                callType,
                displayDialString: `${displayAudioNumber},${meetingRoom}#`,
                dialString: audioNumber,
                meetingCodes: {
                    a0: `${meetingRoom}#`,
                },
            });
        }
        return callMethods;
    }
    parse(event, currentCallMethods) {
        var _a, _b, _c;
        return [
            (_c = (_b = this.getCallMethods((_a = event.location) === null || _a === void 0 ? void 0 : _a.name)) !== null && _b !== void 0 ? _b : this.getCallMethods(getDescription(event))) !== null && _c !== void 0 ? _c : [],
            false,
        ];
    }
}
class TeamsParser {
    constructor() {
        this.parserName = 'Teams';
        this.teamsRegex = /https:\/\/teams.microsoft.com\/l\/meetup-join\/[^/]+\/\d+(?:\?context=[^"]+)/i;
        this.meetingIdRegex = /meeting\s+id:\s+([a-z0-9 ]+)/i;
        this.passcodeRegex = /passcode:\s*([a-zA-Z0-9]+)/i;
    }
    getCallMethods(str) {
        const callMethods = [];
        if (str === undefined)
            return undefined;
        const match = this.teamsRegex.exec(str);
        if (!match)
            return undefined;
        const callMethod = {
            callType: 'teams',
            dialString: match[0],
        };
        const strippedStr = striptags(str);
        const meetingIdMatch = this.meetingIdRegex.exec(strippedStr);
        if (meetingIdMatch) {
            callMethod.displayDialString = meetingIdMatch[1].replace(/\s/g, '');
            const passcodeMatch = this.passcodeRegex.exec(strippedStr);
            if (passcodeMatch) {
                callMethod.meetingCodes = { a0: passcodeMatch[1] };
            }
        }
        callMethods.push(callMethod);
        return callMethods;
    }
    parse(event, currentCallMethods) {
        var _a;
        return [(_a = this.getCallMethods(getDescription(event, false))) !== null && _a !== void 0 ? _a : [], true];
    }
}
class ZoomParser {
    constructor() {
        this.googleCalendarLinkRegex = new RegExp('\\w*zoom\\.(?:us|com)/[js]/\\d+\\.\\.\\.\\s*\\(\\s*id:\\s*(\\d+)', 'ig');
        this.hashedPasscodeRegex = /pwd=(\w+)/gi;
        this.linkRegex = new RegExp('https://\\S*zoom\\.(?:us|com)/[js]/(\\d+)', 'ig');
        this.parserName = 'Zoom';
        this.passcodeRegex = /passcode:\s*(\d+)/gi;
        this.phoneRegex = new RegExp('(' + phoneNumberRegex.source + ')', 'ig');
    }
    getCallMethods(str, where) {
        var _a, _b, _c;
        if (str === undefined)
            return [];
        this.googleCalendarLinkRegex.lastIndex = 0;
        this.hashedPasscodeRegex.lastIndex = 0;
        this.linkRegex.lastIndex = 0;
        this.passcodeRegex.lastIndex = 0;
        this.phoneRegex.lastIndex = 0;
        const callMethods = [];
        let match = (_a = this.linkRegex.exec(str)) !== null && _a !== void 0 ? _a : this.googleCalendarLinkRegex.exec(str);
        if (!match)
            return callMethods;
        const [, meetingId] = match;
        const passcode = (_b = this.passcodeRegex.exec(str)) === null || _b === void 0 ? void 0 : _b[1];
        const hashedPasscode = (_c = this.hashedPasscodeRegex.exec(str)) === null || _c === void 0 ? void 0 : _c[1];
        const callMethod = {
            callType: 'zoom',
            dialString: meetingId,
        };
        if (passcode !== undefined) {
            callMethod.meetingCodes = { a0: passcode };
            if (hashedPasscode)
                callMethod.alternateMeetingCodes = { a0: hashedPasscode };
        }
        else if (hashedPasscode !== undefined) {
            callMethod.meetingCodes = { a0: hashedPasscode };
        }
        callMethods.push(callMethod);
        callMethods.push({
            callType: 'video',
            callTypeDetail: {
                zoomConnector: true,
            },
            dialString: `${meetingId}@zoomcrc.com`,
        });
        if (where === 'location')
            return callMethods;
        let numberOfPhoneNumbers = 0;
        while ((match = this.phoneRegex.exec(str)) !== null) {
            const dialString = match[1].replace(nonTelephonyDigits, '');
            if (dialString === meetingId)
                continue;
            numberOfPhoneNumbers++;
            if (numberOfPhoneNumbers > 2)
                break;
            for (const callType of ['pstn', 'voip']) {
                const callMethod = {
                    callType,
                    displayDialString: `${match[1]},${meetingId}#`,
                    dialString: match[1].replace(nonTelephonyDigits, ''),
                    meetingCodes: { a0: `${meetingId}#` },
                };
                if (passcode && passcode.length < 15) {
                    callMethod.displayDialString += `,${passcode}#`;
                    if (callMethod.meetingCodes === undefined)
                        callMethod.meetingCodes = {};
                    callMethod.meetingCodes.a1 = `${passcode}#`;
                }
                callMethods.push(callMethod);
            }
        }
        return callMethods;
    }
    handleMissingPasscodes(aCallMethods, bCallMethods) {
        let same = false;
        if (bCallMethods === undefined) {
            bCallMethods = aCallMethods;
            same = true;
        }
        for (let i = 0; i < aCallMethods.length; i++) {
            for (let j = same ? i : 0; j < bCallMethods.length; j++) {
                const a = aCallMethods[i];
                const b = bCallMethods[j];
                if (a.callType === 'zoom' &&
                    b.callType === 'zoom' &&
                    a.dialString === b.dialString) {
                    if (a.meetingCodes === undefined && b.meetingCodes !== undefined) {
                        a.meetingCodes = Object.assign({}, b.meetingCodes);
                    }
                    if (a.alternateMeetingCodes === undefined &&
                        b.alternateMeetingCodes !== undefined) {
                        a.alternateMeetingCodes = Object.assign({}, b.alternateMeetingCodes);
                    }
                }
            }
        }
    }
    parse(event, currentCallMethods) {
        var _a;
        const locationCallMethods = this.getCallMethods((_a = event.location) === null || _a === void 0 ? void 0 : _a.name, 'location');
        const descriptionCallMethods = this.getCallMethods(getDescription(event), 'description');
        const callMethods = [
            ...locationCallMethods,
            ...descriptionCallMethods,
        ];
        this.handleMissingPasscodes(callMethods);
        this.handleMissingPasscodes(currentCallMethods, callMethods);
        return [callMethods, descriptionCallMethods.length === 0];
    }
}
const calendarEventParsers = [
    new BlueJeansParser(),
    new ZoomParser(),
    new GoogleMeetParser(),
    new GoToMeetingParser(),
    new PolycomParser(),
    new LyncParser(),
    new PlcmTeamsParser(),
    new TeamsParser(),
    new GeneralParser(),
];
export function getCallMethods(event) {
    var _a;
    // If the event has callMethods, include them first.
    let callMethods = Object.values((_a = event.callMethods) !== null && _a !== void 0 ? _a : {});
    for (const parser of calendarEventParsers) {
        const [newCallMethods, continueParsingOnSuccess] = parser.parse(event, callMethods);
        callMethods.push(...newCallMethods);
        if (newCallMethods.length > 0 && !continueParsingOnSuccess)
            break;
    }
    callMethods = zoomConsolidatePasscodes(callMethods);
    callMethods = zoomRemoveInvalidTelephonyMethods(callMethods);
    callMethods = removeDuplicates(callMethods);
    return Object.fromEntries(callMethods.map((callMethod, i) => [`a${i}`, callMethod]));
}
function getDescription(event, stripHtmlTags = true) {
    if (event.description === undefined)
        return undefined;
    if (event.description.contentType === 'html') {
        return stripHtmlTags
            ? decode(striptags(event.description.content))
            : decode(event.description.content);
    }
    return event.description.content;
}
function removeDuplicates(callMethods) {
    return callMethods.filter((a, i) => {
        for (let j = 0; j < i; j++) {
            const b = callMethods[j];
            if (a.callType === b.callType &&
                a.dialString === b.dialString &&
                objEquals(a.meetingCodes, b.meetingCodes)) {
                return false;
            }
        }
        return true;
    });
}
function zoomConsolidatePasscodes(callMethods) {
    var _a, _b, _c;
    const shortestPasscodes = {};
    const longestPasscodes = {};
    // Find shortest and longest passcodes for each Zoom meeting number (dial string).
    for (const callMethod of callMethods) {
        if (callMethod.callType !== 'zoom')
            continue;
        if (callMethod.dialString === undefined)
            continue;
        const passcode = (_a = callMethod.meetingCodes) === null || _a === void 0 ? void 0 : _a.a0;
        if (passcode === undefined)
            continue;
        const shortestPasscode = shortestPasscodes[callMethod.dialString];
        if (shortestPasscode === undefined || passcode.length < shortestPasscode.length) {
            shortestPasscodes[callMethod.dialString] = passcode;
        }
        const longestPasscode = longestPasscodes[callMethod.dialString];
        if (longestPasscode === undefined || passcode.length < longestPasscode.length) {
            longestPasscodes[callMethod.dialString] = passcode;
        }
    }
    // Set meeting codes to their shortest versions and alternate meeting codes to their longest
    // versions. (Duplicates are removed later in a separate step.)
    for (const callMethod of callMethods) {
        if (callMethod.callType !== 'zoom')
            continue;
        if (callMethod.dialString === undefined)
            continue;
        const shortest = shortestPasscodes[callMethod.dialString];
        const longest = longestPasscodes[callMethod.dialString];
        if (shortest !== undefined && ((_b = callMethod.meetingCodes) === null || _b === void 0 ? void 0 : _b.a0) !== shortest) {
            if (callMethod.meetingCodes === undefined)
                callMethod.meetingCodes = {};
            callMethod.meetingCodes.a0 = shortest;
        }
        if (longest !== undefined && ((_c = callMethod.meetingCodes) === null || _c === void 0 ? void 0 : _c.a0) !== longest) {
            if (callMethod.meetingCodes === undefined)
                callMethod.meetingCodes = {};
            callMethod.meetingCodes.a0 = longest;
        }
    }
    return callMethods;
}
function zoomRemoveInvalidTelephonyMethods(callMethods) {
    const zoomMeetingIds = [];
    for (const callMethod of callMethods) {
        if (callMethod.callType === 'zoom' &&
            callMethod.dialString !== undefined &&
            !zoomMeetingIds.includes(callMethod.dialString)) {
            zoomMeetingIds.push(callMethod.dialString);
        }
    }
    return callMethods.filter(callMethod => !((callMethod.callType === 'pstn' || callMethod.callType === 'voip') &&
        callMethod.dialString &&
        zoomMeetingIds.includes(callMethod.dialString)));
}
